import { useDebounce } from "@uidotdev/usehooks";
import { cn } from "../cn"
import { useEffect, useState } from "react";
import { useScrollControl } from "../Modal";
import { Button } from "../Button";
import { IconClose } from "../Icons";
import { Switch } from "../Switch";
import { SideMenuListSeparator } from "./components/SideMenuListSeparator";

type SideMenuProps = React.PropsWithChildren<{
  show: boolean,
  toggle: () => void,
  logout: () => void,
  lightModeImage: string,
  darkModeImage: string,
  isLoggedIn: boolean,
  firstName?: string,
  email?: string,
  loginTo?: string,
  loginOnPress?: () => void,
  customHeader?: React.ReactNode,
}>;

export const SideMenu = ({children, customHeader, show, toggle, loginTo, loginOnPress, logout, lightModeImage, darkModeImage, isLoggedIn, firstName, email}: SideMenuProps) => {
  const debouncedShow = useDebounce(show, 100);
  useScrollControl(show);

  return (<>
    <div 
      className={cn(
        'fixed top-0 bottom-0 left-0 right-0 z-40 bg-neutral-300/50 pointer-events-none opacity-0 transition-all',
        show && 'opacity-100',
        debouncedShow && 'pointer-events-auto',
      )} onClick={toggle}
    />
    <div className={cn(
      'flex flex-col fixed top-0 right-0 h-full w-screen sm:w-[328px] bg-surface dark:bg-surface-dark z-[1000001] transform translate-x-[110%] transition-transform duration-300 overflow-y-scroll',
      show && 'translate-x-0',
    )} style={{boxShadow: '-4px 0px 6px 0px rgba(0, 0, 0, 0.1)'}}>
      <div className='absolute top-5 right-4 flex justify-end gap-2'>
        <Button unstyled onPress={toggle} className='w-9 h-9 rounded-full justify-center items-center flex' excludeFromTabOrder={!show}>
          <IconClose/>
        </Button>
      </div>
      <div className='py-[9px] pt-6 px-3 gap-1 flex flex-col items-start'>
        <img src={lightModeImage} className='block sm:hidden h-16 mb-6 dark:hidden' aria-label='Rock Island Auction Company Logo'/>
        <img src={darkModeImage} alt='Rock Island Auction Company' className='h-16 mb-6 hidden dark:block dark:sm:hidden' aria-label='Rock Island Auction Company Logo'/>
        {customHeader && customHeader ||<>
          {isLoggedIn && <>
            <div className='font-bold text-lg leading-6 tracking-wide'>Hello, {firstName}.</div>
            <div className='font-normal text-text-light text-sm leading-5 tracking-wide'>{email}</div>
          </> || <>
            <div className='font-bold text-lg leading-6 tracking-wide'>Hello.</div>
            <div className='text-sm leading-5 tracking-wide'>
              Please <Button variant='link' className='p-0 no-underline hover:underline' to='/login' onPress={toggle} excludeFromTabOrder={!show}>Log In</Button>
              {', or '}
              <Button variant='link' className='p-0 no-underline hover:underline' onPress={() => window.location.pathname = '/create-account'} tabIndex={!show ? -1 : 0}>Create an account</Button>.
            </div>
          </>}
        </>}
      </div>
      <SideMenuListSeparator/>
      {children}
    </div>
  </>)
}